import { useEffect, useState } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Drawer, FieldText, FieldTextarea } from "@/pages/Clients";
import {
  PlusIcon,
  TrashIcon,
  UploadSimpleIcon,
  FilePdfIcon,
  FileXIcon,
  EyeIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function fileToText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsText(file);
  });
}

function InvoiceForm({ initial, onSave, onCancel }) {
  const [data, setData] = useState(
    initial || {
      number: "",
      supplier: "",
      value: 0,
      issued_at: "",
      products: [],
      pdf_data: "",
      xml_data: "",
      notes: "",
    }
  );
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const handleXml = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const text = await fileToText(file);
      const b64 = await fileToBase64(file);
      const res = await api.post("/invoices/parse-xml", { xml: text });
      const p = res.data;
      setData((prev) => ({
        ...prev,
        xml_data: b64,
        number: p.number || prev.number,
        supplier: p.supplier || prev.supplier,
        value: p.value || prev.value,
        issued_at: p.issued_at || prev.issued_at,
        products: p.products || [],
      }));
      toast.success("XML lido com sucesso");
    } catch (err) {
      toast.error("Não foi possível ler o XML");
    } finally {
      setBusy(false);
    }
  };

  const handlePdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setData({ ...data, pdf_data: await fileToBase64(file) });
    toast.success("PDF anexado");
  };

  const submit = async (e) => {
    e.preventDefault();
    await onSave({ ...data, value: Number(data.value) || 0 });
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="invoice-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="border border-dashed border-white/15 rounded-lg p-5 hover:border-blue-500/40 hover:bg-white/5 transition cursor-pointer flex flex-col items-center justify-center text-center">
          <FileXIcon size={32} weight="duotone" className="text-blue-400" />
          <span className="text-sm font-medium mt-2">XML da NF-e</span>
          <span className="text-xs text-slate-500 mt-1">{data.xml_data ? "✓ Anexado" : "Clique para enviar"}</span>
          <input type="file" accept=".xml,text/xml" onChange={handleXml} className="hidden" data-testid="invoice-xml-input" />
        </label>
        <label className="border border-dashed border-white/15 rounded-lg p-5 hover:border-blue-500/40 hover:bg-white/5 transition cursor-pointer flex flex-col items-center justify-center text-center">
          <FilePdfIcon size={32} weight="duotone" className="text-rose-400" />
          <span className="text-sm font-medium mt-2">PDF da Nota</span>
          <span className="text-xs text-slate-500 mt-1">{data.pdf_data ? "✓ Anexado" : "Clique para enviar"}</span>
          <input type="file" accept="application/pdf" onChange={handlePdf} className="hidden" data-testid="invoice-pdf-input" />
        </label>
      </div>

      {busy && <div className="text-sm text-blue-400">Lendo XML...</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldText label="Número *" value={data.number} onChange={set("number")} required testid="invoice-number-input" />
        <FieldText label="Fornecedor *" value={data.supplier} onChange={set("supplier")} required testid="invoice-supplier-input" />
        <FieldText label="Valor (R$) *" type="number" step="0.01" value={data.value} onChange={set("value")} required testid="invoice-value-input" />
        <FieldText label="Data de emissão" value={data.issued_at || ""} onChange={set("issued_at")} testid="invoice-date-input" />
      </div>

      {data.products?.length > 0 && (
        <div className="border border-white/10 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">Produtos lidos do XML ({data.products.length})</div>
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr><th className="text-left py-1">Cód</th><th className="text-left py-1">Produto</th><th className="text-right py-1">Qtd</th><th className="text-right py-1">Total</th></tr>
              </thead>
              <tbody>
                {data.products.map((p, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-1 font-mono text-blue-400">{p.code}</td>
                    <td className="py-1">{p.name}</td>
                    <td className="py-1 text-right">{p.quantity}</td>
                    <td className="py-1 text-right text-emerald-400">{brl(p.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <FieldTextarea label="Observações" value={data.notes} onChange={set("notes")} testid="invoice-notes-input" />

      <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 transition" data-testid="invoice-cancel-btn">
          Cancelar
        </button>
        <button type="submit" disabled={busy} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium shadow-[0_0_15px_rgba(37,99,235,0.4)] transition" data-testid="invoice-save-btn">
          Salvar nota
        </button>
      </div>
    </form>
  );
}

function ViewInvoice({ id, onClose }) {
  const [inv, setInv] = useState(null);
  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data));
  }, [id]);

  if (!inv) return <div className="text-slate-400">Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><div className="text-xs text-slate-500">Número</div><div className="font-mono">{inv.number}</div></div>
        <div><div className="text-xs text-slate-500">Fornecedor</div><div>{inv.supplier}</div></div>
        <div><div className="text-xs text-slate-500">Valor</div><div className="text-emerald-400 font-bold">{brl(inv.value)}</div></div>
        <div><div className="text-xs text-slate-500">Emissão</div><div>{inv.issued_at || "—"}</div></div>
      </div>
      {inv.pdf_data && (
        <div className="border border-white/10 rounded-lg overflow-hidden h-[600px]">
          <iframe src={inv.pdf_data} title="NF PDF" className="w-full h-full bg-white" />
        </div>
      )}
      {inv.products?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Produtos</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500"><tr><th className="text-left py-1">Cód</th><th className="text-left py-1">Produto</th><th className="text-right py-1">Qtd</th><th className="text-right py-1">Total</th></tr></thead>
            <tbody>
              {inv.products.map((p, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-1 font-mono text-blue-400">{p.code}</td>
                  <td className="py-1">{p.name}</td>
                  <td className="py-1 text-right">{p.quantity}</td>
                  <td className="py-1 text-right text-emerald-400">{brl(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end pt-2 border-t border-white/5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 transition">Fechar</button>
      </div>
    </div>
  );
}

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/invoices");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      await api.post("/invoices", data);
      toast.success("Nota fiscal salva");
      setOpen(false);
      load();
    } catch {
      toast.error("Erro ao salvar nota");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta nota?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Nota removida");
    load();
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Notas Fiscais"
        subtitle="Anexe PDFs e XMLs de NF-e. A leitura do XML é automática."
        actions={
          <button
            onClick={() => setOpen(true)}
            data-testid="new-invoice-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-[0_0_15px_rgba(37,99,235,0.4)] transition hover:-translate-y-0.5"
          >
            <UploadSimpleIcon size={16} weight="bold" /> Nova nota
          </button>
        }
      />

      <div className="glass rounded-xl p-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <tr>
                <th className="py-3 pr-4">Número</th>
                <th className="py-3 pr-4">Fornecedor</th>
                <th className="py-3 pr-4">Valor</th>
                <th className="py-3 pr-4">Emissão</th>
                <th className="py-3 pr-4">Cadastrada em</th>
                <th className="py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`invoice-row-${i.id}`}>
                  <td className="py-3 pr-4 font-mono text-blue-400">{i.number}</td>
                  <td className="py-3 pr-4 font-medium">{i.supplier}</td>
                  <td className="py-3 pr-4 text-emerald-400 font-medium">{brl(i.value)}</td>
                  <td className="py-3 pr-4 text-slate-400">{i.issued_at ? i.issued_at.slice(0, 10) : "—"}</td>
                  <td className="py-3 pr-4 text-slate-400">{fmtDate(i.created_at)}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setViewing(i.id)} className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-blue-400 transition" data-testid={`view-invoice-${i.id}`}>
                        <EyeIcon size={16} />
                      </button>
                      <button onClick={() => remove(i.id)} className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-rose-400 transition" data-testid={`delete-invoice-${i.id}`}>
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-slate-500">
                  Nenhuma nota fiscal cadastrada. Clique em "Nova nota" para começar.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer open={open} title="Nova nota fiscal" onClose={() => setOpen(false)} size="lg">
        <InvoiceForm onSave={save} onCancel={() => setOpen(false)} />
      </Drawer>

      <Drawer open={!!viewing} title="Visualizar nota fiscal" onClose={() => setViewing(null)} size="xl">
        {viewing && <ViewInvoice id={viewing} onClose={() => setViewing(null)} />}
      </Drawer>
    </div>
  );
}
